import { useState } from 'react';
import { X, CircleNotch, Warning } from '@phosphor-icons/react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * VoidReasonModal — confirm a financial void and capture the reason the server demands.
 *
 * ── WHY THIS IS NOT `DeleteConfirmModal` ──────────────────────────────────────────────────
 * The shared destructive-action dialog takes a title, a message and a confirm label and
 * returns a yes/no. Both void endpoints REQUIRE a free-text reason — `reason` min 1 / max 500
 * on `POST /payments/:id/void` (payments.schema.ts:88-90) and `voidReason` min 5 / max 2000 on
 * `POST /credit-notes/:id/void` (credit-notes.schema.ts:17-18). A yes/no dialog cannot satisfy
 * either contract, and widening the shared one would change the behaviour of every list in the
 * product that uses `RowActions`. So this is local to the money surface and does nothing else.
 *
 * The bounds are PROPS, not constants, and each caller passes the figures from its own
 * endpoint's Zod schema. They are a courtesy to the user — the server re-validates and stays
 * authoritative; this dialog exists so a barrister is told what is wrong before the round trip,
 * not after it.
 */

interface Props {
  open: boolean;
  /** Dialog heading, e.g. "Void payment". Also the dialog's accessible name. */
  title: string;
  /** What voiding this record will do, in the user's terms. */
  description: string;
  /** Label above the reason field. */
  reasonLabel: string;
  minLength: number;
  maxLength: number;
  placeholder?: string;
  /** Shown while the mutation is in flight; also locks the dialog. */
  pending?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function VoidReasonModal({
  open,
  title,
  description,
  reasonLabel,
  minLength,
  maxLength,
  placeholder,
  pending = false,
  onConfirm,
  onCancel,
}: Props) {
  const [reason, setReason] = useState('');

  if (!open) return null;

  const trimmed = reason.trim();
  // Validated against the trimmed value because that is what is sent — a field of spaces
  // would otherwise pass here and be rejected by the server.
  const tooShort = trimmed.length > 0 && trimmed.length < minLength;
  const canSubmit = trimmed.length >= minLength && reason.length <= maxLength && !pending;

  const close = () => {
    if (pending) return;
    setReason('');
    onCancel();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <ScrollLock />
      <Card className="my-auto flex w-full max-w-md flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <Warning size={15} weight="duotone" className="text-red-600 dark:text-red-400" />
            {title}
          </h2>
          <button
            type="button"
            onClick={close}
            disabled={pending}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-5 py-4">
          <p className="text-xs text-muted-foreground">{description}</p>

          <div>
            <label className="mb-1 block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              {reasonLabel}
              <span className="ml-1 font-normal normal-case tracking-normal text-muted-foreground/70">
                (required{minLength > 1 ? `, min ${minLength} characters` : ''} — recorded on the audit trail)
              </span>
            </label>
            {/* A plain textarea, not `ExpandableTextarea`.
                That component is readonly inline and opens a SECOND modal to edit
                (expandable-textarea.tsx:63-71) — a modal inside a confirm dialog, with its own
                Escape handler competing with this one, for a one-line reason. The part of the
                CreditNoteModal pattern worth reusing is the VALIDATION UX — min-length error,
                live counter, trimmed check — and that is reused below. */}
            <textarea
              id="void-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={maxLength}
              placeholder={placeholder}
              disabled={pending}
              aria-label={reasonLabel}
              className="w-full resize-none rounded-md border border-border bg-card px-3 py-2 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-600/40"
            />
            {tooShort && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                Give at least {minLength} characters.
              </p>
            )}
            <p className="mt-1 text-right text-[10px] text-muted-foreground/60">
              {reason.length}/{maxLength}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={close} disabled={pending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={!canSubmit}
            onClick={() => onConfirm(trimmed)}
          >
            {pending && <CircleNotch size={13} className="mr-1.5 animate-spin" />}
            {title}
          </Button>
        </div>
      </Card>
    </div>
  );
}
